/**
 * useTenantSafeQuery — `useQuery` wrapper for query keys that fold in
 * `useTenant().selectedTenantId` (Prompt 11F.8).
 *
 * A tenant-qualified key (e.g. `['config', 'tax', selectedTenantId]`)
 * already refetches on its own when the tenant switches — new key, no
 * cache entry, `useQuery` fetches immediately. The problem is
 * `TenantProvider`'s OWN tenant-switch effect also calls
 * `queryClient.clear()` in the same commit (see `TenantContext.tsx`) to
 * flush every NON-tenant-qualified query elsewhere in the app. `clear()`
 * calls `Query.destroy()` on every query in the cache, including the
 * brand-new one this hook's key change just created — `destroy()` cancels
 * its in-flight fetch (`cancel({ silent: true })`), so the response that
 * lands a moment later has nowhere to go: react-query silently drops it,
 * and — because the key never changes again — nothing ever refetches.
 * The UI is left permanently showing the PREVIOUS tenant's data.
 *
 * Fix: after a tenant switch, explicitly call the observer's own
 * `refetch()` one macrotask later (`setTimeout(..., 0)`). Every effect
 * queued by the same commit — including `TenantProvider`'s `clear()` —
 * has already flushed by the time a macrotask runs, so this refetch
 * starts with a clean cache and can never be cancelled by it. Calling the
 * observer's `refetch()` (rather than `queryClient.refetchQueries()`,
 * which only matches an EXISTING cache entry — already removed by
 * `clear()` by this point, so it would silently match nothing) works
 * because the observer itself is responsible for ensuring a query exists
 * for its current key.
 */

import { useEffect, useRef } from 'react';
import { useQuery, type QueryKey, type UseQueryOptions } from '@tanstack/react-query';
import { useTenant } from './TenantContext';

export function useTenantSafeQuery<TData, TKey extends QueryKey = QueryKey>(
  options: UseQueryOptions<TData, Error, TData, TKey>,
) {
  const { selectedTenantId } = useTenant();
  const query = useQuery(options);
  // `refetch` lets the effect below call back into the OBSERVER itself
  // (not `queryClient.refetchQueries`, which only acts on an EXISTING
  // cache entry — by the time this fires, `clear()` has already removed
  // it, so there'd be nothing to match and refetchQueries would no-op).
  const refetchRef = useRef(query.refetch);
  refetchRef.current = query.refetch;

  useEffect(() => {
    const timer = setTimeout(() => {
      void refetchRef.current();
    }, 0);
    return () => clearTimeout(timer);
    // Re-run only when the tenant actually changes — refetchRef.current
    // always holds the latest `refetch`, so it's deliberately not a dep.
  }, [selectedTenantId]);

  return query;
}
